import { create } from "zustand"

import type { ProjectFileGeneration } from "@/features/projects/utils/project-file-status"
import type { ProjectFile } from "@/services/project-service"

import {
  hasUnobservedProjectFileGenerations,
  mergeProjectFileGenerations,
} from "@/features/projects/utils/project-file-status"

type ProjectFileUploadRefreshStore = {
  pendingByProject: Partial<Record<string, ProjectFileUploadRefresh>>
  register: (projectId: string, files: readonly ProjectFileGeneration[]) => void
  settleObserved: (projectId: string, files: readonly ProjectFile[] | undefined) => void
  expire: (projectId: string, expiresAt: number) => void
  reset: () => void
}

export type ProjectFileUploadRefresh = {
  generations: ProjectFileGeneration[]
  expiresAt: number
}

export const PROJECT_FILE_UPLOAD_REFRESH_TTL_MS = 6 * 60 * 1000

export const useProjectFileUploadRefreshStore = create<ProjectFileUploadRefreshStore>((set) => ({
  pendingByProject: {},

  register: (projectId, files) => {
    if (files.length === 0) return
    set((state) => ({
      pendingByProject: {
        ...state.pendingByProject,
        [projectId]: {
          generations: mergeProjectFileGenerations(
            state.pendingByProject[projectId]?.generations ?? [],
            files
          ),
          expiresAt: Date.now() + PROJECT_FILE_UPLOAD_REFRESH_TTL_MS,
        },
      },
    }))
  },

  settleObserved: (projectId, files) => {
    set((state) => {
      const pendingEntry = state.pendingByProject[projectId]
      if (!pendingEntry) return state
      const pending = pendingEntry.generations

      const remaining = pending.filter((generation) =>
        hasUnobservedProjectFileGenerations(files, [generation])
      )
      if (remaining.length === pending.length) return state

      const pendingByProject = { ...state.pendingByProject }
      if (remaining.length > 0) {
        pendingByProject[projectId] = {
          generations: remaining,
          expiresAt: pendingEntry.expiresAt,
        }
      } else delete pendingByProject[projectId]
      return { pendingByProject }
    })
  },

  expire: (projectId, expiresAt) => {
    set((state) => {
      const pending = state.pendingByProject[projectId]
      if (!pending || pending.expiresAt !== expiresAt || pending.expiresAt > Date.now())
        return state
      const pendingByProject = { ...state.pendingByProject }
      delete pendingByProject[projectId]
      return { pendingByProject }
    })
  },

  reset: () => set({ pendingByProject: {} }),
}))
